import { inject, Injectable, signal } from '@angular/core';

import { firstValueFrom } from 'rxjs';

import { TellentAssistantHttp } from '@core/tellent-assistant-data';
import type { ResolvedResultLink } from '@core/tellent-assistant-types';
import {
  AppKind,
  ORG_GUID,
  type TellentAppRouteID,
  type TellentPageConfig,
} from '@core/tellent-types';
import { injectTellentAppUrlBuilder } from '@core/tellent-util';

export type ActionResolution = 'confirmed' | 'cancelled';

export interface ActionResultLink {
  label: string;
  url: string;
}

/** The reason goes into the model-facing outcome report — cap it so a large error body doesn't flood the transcript. */
const MAX_ERROR_REASON_LENGTH = 300;

/** Best-effort human-readable reason from an HTTP error response. */
const extractErrorReason = (error: unknown): string => {
  const body = (<{ error?: unknown }>error)?.error;
  if (typeof body === 'string') return body;
  if (body && typeof body === 'object') {
    const reason =
      (<{ error?: unknown; reason?: unknown; errors?: unknown }>body).error ??
      (<{ reason?: unknown }>body).reason ??
      (<{ errors?: unknown }>body).errors;
    if (typeof reason === 'string') return reason;
    if (reason !== undefined) return JSON.stringify(reason).slice(0, MAX_ERROR_REASON_LENGTH);
  }
  const message = (<{ message?: unknown }>error)?.message;
  return typeof message === 'string' ? message : 'unknown error';
};

/**
 * Dispatches user-confirmed assistant actions to the backend (executed via the
 * owning product's front-door route, as the acting user) and tracks how each
 * proposal was resolved so the decision card renders as already-decided.
 *
 * The backend is the source of truth: resolutions are keyed by proposalId and
 * scoped to a thread, so `loadForThread` re-hydrates them on reload / any device.
 *
 * Decisions happen OUTSIDE the agent conversation, so the agent is blind to
 * them: each outcome is also queued as a report line that the send path
 * delivers as a hidden UI-event user message ahead of the next user message
 * (see `drainOutcomeReport`) — the agent learns what was confirmed, declined,
 * or failed (and why), and can correct a failed proposal's params.
 */
@Injectable({ providedIn: 'root' })
export class TellentAssistantActionsService {
  private readonly http = inject(TellentAssistantHttp);
  private readonly buildAppUrl = injectTellentAppUrlBuilder();
  private readonly orgGuid = inject(ORG_GUID, { optional: true });
  private readonly resolutions = signal<Record<string, ActionResolution>>({});
  private readonly resultLinks = signal<Record<string, ActionResultLink>>({});
  private readonly loadedThreads = new Set<string>();
  private readonly unreportedOutcomes = new Map<string, string[]>();

  public async execute(
    threadId: string,
    proposalId: string,
    action: string,
    params: Record<string, unknown>,
  ): Promise<void> {
    let response: { result?: unknown; resultLink?: ResolvedResultLink | null };
    try {
      response = await firstValueFrom(
        this.http.executeAction(threadId, proposalId, action, params),
      );
    } catch (error) {
      this.queueOutcome(
        threadId,
        `The user CONFIRMED the proposed action \`${action}\` (proposal ${proposalId}), but execution FAILED: ${extractErrorReason(error)}. The action was NOT performed. If the user still wants it done, fix the parameters and propose it again.`,
      );
      throw error;
    }
    this.resolutions.update(current => ({ ...current, [proposalId]: 'confirmed' }));
    this.storeResultLink(proposalId, response.resultLink);
    this.queueOutcome(
      threadId,
      `The user CONFIRMED the proposed action \`${action}\` (proposal ${proposalId}) and it executed successfully.`,
    );
  }

  public resultLinkOf(proposalId: string): ActionResultLink | null {
    return this.resultLinks()[proposalId] ?? null;
  }

  public markCancelled(threadId: string, proposalId: string, action: string): void {
    // Optimistic — but revert on failure so the card truthfully reverts to
    // actionable (an unrecorded cancel would resurrect on reload anyway).
    this.resolutions.update(current => ({ ...current, [proposalId]: 'cancelled' }));
    this.queueOutcome(
      threadId,
      `The user DECLINED the proposed action \`${action}\` (proposal ${proposalId}). It was NOT executed. Do not re-propose it unless the user asks again.`,
    );
    firstValueFrom(this.http.cancelAction(threadId, proposalId)).catch(() => {
      this.resolutions.update(current => {
        const { [proposalId]: _reverted, ...rest } = current;
        return rest;
      });
    });
  }

  /**
   * Returns (and clears) the outcome report for a thread — the send path
   * delivers it as a hidden UI-event user message ahead of the next user
   * message so the agent learns how its proposals were resolved.
   * Session-scoped: outcomes decided before a reload are not re-reported.
   */
  public drainOutcomeReport(threadId: string): string | null {
    const lines = this.unreportedOutcomes.get(threadId);
    if (!lines || lines.length === 0) return null;
    this.unreportedOutcomes.delete(threadId);
    return [
      'Update on your proposed actions (decided by the user in the confirmation UI):',
      ...lines.map(line => `- ${line}`),
    ].join('\n');
  }

  public resolutionOf(proposalId: string): ActionResolution | null {
    return this.resolutions()[proposalId] ?? null;
  }

  /**
   * Load a thread's resolutions from the backend and merge them in (session
   * state wins). Fetches once per thread per session — repeat calls are free.
   */
  public async loadForThread(threadId: string): Promise<void> {
    if (this.loadedThreads.has(threadId)) return;
    this.loadedThreads.add(threadId);

    try {
      const items = await firstValueFrom(this.http.fetchActionResolutions(threadId));
      const loaded: Record<string, ActionResolution> = {};
      for (const item of items) {
        loaded[item.proposalId] = item.status;
        this.storeResultLink(item.proposalId, item.resultLink);
      }
      this.resolutions.update(current => ({ ...loaded, ...current }));
    } catch (error) {
      this.loadedThreads.delete(threadId); // allow a retry on the next visit
      throw error;
    }
  }

  private storeResultLink(proposalId: string, link: ResolvedResultLink | null | undefined): void {
    if (!link?.route || !link.params) return;

    const url = this.buildAppUrl(
      AppKind.Recruitee,
      <TellentPageConfig<TellentAppRouteID>>{ id: link.route, params: link.params },
      this.orgGuid ?? undefined,
    );
    if (!url) return;

    this.resultLinks.update(current => ({
      ...current,
      [proposalId]: { label: link.label ?? 'Open', url },
    }));
  }

  private queueOutcome(threadId: string, line: string): void {
    const lines = this.unreportedOutcomes.get(threadId) ?? [];
    this.unreportedOutcomes.set(threadId, [...lines, line]);
  }
}
